/*
* === ApplicationFormJBMarks.js (ARForms Compliant - Smart Pre-Fill & Financials Restored) ===
*/
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
    Container, Box, Stepper, Step, StepLabel, Button, Typography, 
    CircularProgress, Paper, Alert, Snackbar, Grid, TextField, 
    FormControlLabel, Checkbox, RadioGroup, Radio, FormControl, FormLabel,
    InputLabel, Select, MenuItem, Autocomplete, FormHelperText, Divider,
    List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import ReviewStep from '../../application-steps/ReviewStep';

// --- DATA LISTS ---
const LOCATION_OPTIONS = [
    { label: "--- South African Provinces ---", isHeader: true },
    { label: "Eastern Cape", value: "Eastern Cape" },
    { label: "Free State", value: "Free State" },
    { label: "Gauteng", value: "Gauteng" },
    { label: "KwaZulu-Natal", value: "KwaZulu-Natal" },
    { label: "Limpopo", value: "Limpopo" },
    { label: "Mpumalanga", value: "Mpumalanga" },
    { label: "Northern Cape", value: "Northern Cape" },
    { label: "North West", value: "North West" },
    { label: "Western Cape", value: "Western Cape" },
    { label: "--- SADC Countries (Non-SA) ---", isHeader: true },
    { label: "Angola", value: "Angola" },
    { label: "Botswana", value: "Botswana" },
    { label: "Comoros", value: "Comoros" },
    { label: "Democratic Republic of Congo (DRC)", value: "DRC" },
    { label: "Eswatini", value: "Eswatini" },
    { label: "Lesotho", value: "Lesotho" },
    { label: "Madagascar", value: "Madagascar" },
    { label: "Malawi", value: "Malawi" },
    { label: "Mauritius", value: "Mauritius" },
    { label: "Mozambique", value: "Mozambique" },
    { label: "Namibia", value: "Namibia" },
    { label: "Seychelles", value: "Seychelles" },
    { label: "Tanzania", value: "Tanzania" },
    { label: "Zambia", value: "Zambia" },
    { label: "Zimbabwe", value: "Zimbabwe" },
    { label: "--- Other ---", isHeader: true },
    { label: "Other / Outside SADC", value: "Other" }
];

const NUM_REGIONS = [
    "Head Office", "Carletonville", "Eastern Cape", "Free State", 
    "Highveld", "Kimberly", "Kwa-Zulu Natal", "Matlosana", 
    "MDA -  Botswana", "MDA - Eswatini", "MDA – Lesotho", "MDA - Mozambique",
    "MIT Family (JB Marks, EBMTC, EBMTC Academy, SATRI, NUMPROP, MDA, MIC & MIT)", 
    "North East", "PWV", "Rustenburg", "Western Cape", "Other"
];

const QUALIFICATION_TYPES = ["National Certificate", "National Diploma", "Degree", "Honours", "Masters", "PhD/Doctorate"];
const INSTITUTION_TYPES = ["College", "University", "University of Technology"];

const INSTITUTIONS = [
    // ==========================================
    // --- SOUTH AFRICAN PUBLIC UNIVERSITIES ---
    // ==========================================
    { label: "University of Cape Town (UCT)", category: "University" },
    { label: "University of the Witwatersrand (Wits)", category: "University" },
    { label: "Stellenbosch University", category: "University" },
    { label: "University of Pretoria (UP)", category: "University" },
    { label: "University of Johannesburg (UJ)", category: "University" },
    { label: "University of KwaZulu-Natal (UKZN)", category: "University" },
    { label: "University of the Western Cape (UWC)", category: "University" },
    { label: "Rhodes University", category: "University" },
    { label: "North-West University (NWU)", category: "University" },
    { label: "University of the Free State (UFS)", category: "University" },
    { label: "Nelson Mandela University (NMU)", category: "University" },
    { label: "University of Fort Hare", category: "University" },
    { label: "University of Limpopo", category: "University" },
    { label: "University of Venda", category: "University" },
    { label: "Walter Sisulu University (WSU)", category: "University" },
    { label: "University of Zululand", category: "University" },
    { label: "Sefako Makgatho Health Sciences University", category: "University" },
    { label: "University of Mpumalanga", category: "University" },
    { label: "Sol Plaatje University", category: "University" },
    { label: "University of South Africa (UNISA)", category: "University" },
    { label: "Cape Peninsula University of Technology (CPUT)", category: "University of Technology" },
    { label: "Durban University of Technology (DUT)", category: "University of Technology" },
    { label: "Tshwane University of Technology (TUT)", category: "University of Technology" },
    { label: "Vaal University of Technology (VUT)", category: "University of Technology" },
    { label: "Central University of Technology (CUT)", category: "University of Technology" },
    { label: "Mangosuthu University of Technology (MUT)", category: "University of Technology" },

    // ====================================================================
    // --- SOUTH AFRICAN PRIVATE HIGHER EDUCATION INSTITUTIONS (PHEIs) ---
    // ====================================================================
    { label: "Rosebank College (The IIE)", category: "Private Institution" },
    { label: "Varsity College (The IIE)", category: "Private Institution" },
    { label: "Eduvos", category: "Private Institution" },
    { label: "Richfield Graduate Institute of Technology", category: "Private Institution" },
    { label: "Boston City Campus", category: "Private Institution" },
    { label: "CTU Training Solutions", category: "Private Institution" },
    { label: "MANCOSA", category: "Private Institution" },
    { label: "STADIO Higher Education", category: "Private Institution" },
    { label: "AFDA", category: "Private Institution" },
    { label: "Vega School (The IIE)", category: "Private Institution" },
    { label: "Inscape", category: "Private Institution" },
    { label: "South African College of Applied Psychology (SACAP)", category: "Private Institution" },
    { label: "International Hotel School", category: "Private Institution" },
    { label: "TSIBA Education", category: "Private Institution" },
    { label: "IIE Emeris Ruimsig (Roodepoort)", category: "Private Institution" },
{ label: "The Engineering Institute of Technology (EIT)", category: "Private Institution" },

    // ==========================================
    // --- SOUTH AFRICAN PUBLIC TVET COLLEGES ---
    // ==========================================
    // Eastern Cape
    { label: "Buffalo City TVET College", category: "TVET College" },
    { label: "Eastcape Midlands TVET College", category: "TVET College" },
    { label: "Ikhala TVET College", category: "TVET College" },
    { label: "Ingwe TVET College", category: "TVET College" },
    { label: "King Hintsa TVET College", category: "TVET College" },
    { label: "King Sabata Dalindyebo TVET College", category: "TVET College" },
    { label: "Lovedale TVET College", category: "TVET College" },
    { label: "Port Elizabeth TVET College", category: "TVET College" },
    // Free State
    { label: "Flavius Mareka TVET College", category: "TVET College" },
    { label: "Goldfields TVET College", category: "TVET College" },
    { label: "Maluti TVET College", category: "TVET College" },
    { label: "Motheo TVET College", category: "TVET College" },
    // Gauteng
    { label: "Central Johannesburg TVET College", category: "TVET College" },
    { label: "Ekurhuleni East TVET College", category: "TVET College" },
    { label: "Ekurhuleni West TVET College", category: "TVET College" },
    { label: "Sedibeng TVET College", category: "TVET College" },
    { label: "South West Gauteng TVET College", category: "TVET College" },
    { label: "Tshwane North TVET College", category: "TVET College" },
    { label: "Tshwane South TVET College", category: "TVET College" },
    { label: "Western TVET College", category: "TVET College" },
    // KwaZulu-Natal
    { label: "Coastal TVET College", category: "TVET College" },
    { label: "Elangeni TVET College", category: "TVET College" },
    { label: "Esayidi TVET College", category: "TVET College" },
    { label: "Majuba TVET College", category: "TVET College" },
    { label: "Mnambithi TVET College", category: "TVET College" },
    { label: "Mthashana TVET College", category: "TVET College" },
    { label: "Thekwini TVET College", category: "TVET College" },
    { label: "Umfolozi TVET College", category: "TVET College" },
    { label: "Umgungundlovu TVET College", category: "TVET College" },
    // Limpopo
    { label: "Capricorn TVET College", category: "TVET College" },
    { label: "Lephalale TVET College", category: "TVET College" },
    { label: "Letaba TVET College", category: "TVET College" },
    { label: "Mopani South East TVET College", category: "TVET College" },
    { label: "Sekhukhune TVET College", category: "TVET College" },
    { label: "Vhembe TVET College", category: "TVET College" },
    { label: "Waterberg TVET College", category: "TVET College" },
    // Mpumalanga
    { label: "Ehlanzeni TVET College", category: "TVET College" },
    { label: "Gert Sibande TVET College", category: "TVET College" },
    { label: "Nkangala TVET College", category: "TVET College" },
    // Northern Cape & North West
    { label: "Northern Cape Rural TVET College", category: "TVET College" },
    { label: "Northern Cape Urban TVET College", category: "TVET College" },
    { label: "Orbit TVET College", category: "TVET College" },
    { label: "Taletso TVET College", category: "TVET College" },
    { label: "Vuselela TVET College", category: "TVET College" },
    // Western Cape
    { label: "Boland TVET College", category: "TVET College" },
    { label: "College of Cape Town for TVET", category: "TVET College" },
    { label: "False Bay TVET College", category: "TVET College" },
    { label: "Northlink TVET College", category: "TVET College" },
    { label: "South Cape TVET College", category: "TVET College" },
    { label: "West Coast TVET College", category: "TVET College" },

    // ==========================================
    // --- LESOTHO ---
    // ==========================================
    { label: "National University of Lesotho (NUL)", category: "University" },
    { label: "Limkokwing University of Creative Technology (Lesotho)", category: "University" },
    { label: "Botho University Lesotho", category: "University" },
    { label: "Lerotholi Polytechnic", category: "College" },
    { label: "Lesotho Agricultural College (LAC)", category: "College" },

    // ==========================================
    // --- ESWATINI (SWAZILAND) ---
    // ==========================================
    { label: "University of Eswatini", category: "University" },
    { label: "Southern African Nazarene University", category: "University" },
    { label: "Eswatini Medical Christian University", category: "University" },
    { label: "Limkokwing University of Creative Technology (Eswatini)", category: "University" },
    { label: "Eswatini College of Technology", category: "College" },

    // ==========================================
    // --- BOTSWANA ---
    // ==========================================
    { label: "University of Botswana (UB)", category: "University" },
    { label: "Botswana International University of Science and Technology (BIUST)", category: "University" },
    { label: "Botho University (Botswana)", category: "University" },
    { label: "BA ISAGO University", category: "University" },
    { label: "Botswana University of Agriculture and Natural Resources", category: "University" },
    { label: "Botswana Open University", category: "University" },
    { label: "Botswana Accountancy College", category: "College" },
    { label: "Limkokwing University of Creative Technology (Botswana)", category: "University" },

    // ==========================================
    // --- MOZAMBIQUE ---
    // ==========================================
    { label: "Eduardo Mondlane University", category: "University" },
    { label: "Catholic University of Mozambique", category: "University" },
    { label: "Maputo University", category: "University" },
    { label: "Lúrio University", category: "University" },
    { label: "Zambeze University", category: "University" },
    { label: "Joaquim Chissano University", category: "University" },
    { label: "Polytechnic University (Universidade Politécnica)", category: "University" },
    { label: "Technical University of Mozambique", category: "University" },
    { label: "St. Thomas University of Mozambique", category: "University" },

    // ==========================================
    // --- SADC TVET & VOCATIONAL COLLEGES ---
    // ==========================================
    // Lesotho TVETs
    { label: "Lerotholi Polytechnic", category: "TVET College" },
    { label: "Lesotho Agricultural College (LAC)", category: "TVET College" },
    { label: "Technical School of Leloaleng", category: "TVET College" },
    { label: "Thaba-Tseka Technical Institute", category: "TVET College" },
    { label: "St. Elizabeth Technical Institute", category: "TVET College" },
    { label: "Bethel Business Community Development Centre", category: "TVET College" },
    { label: "Catholic Comprehensive Community College (CCCC)", category: "TVET College" },

    // Eswatini (Swaziland) TVETs
    { label: "Eswatini College of Technology (ECOT)", category: "TVET College" },
    { label: "Gwamile Vocational and Commercial Training Institute (VOCTIM)", category: "TVET College" },
    { label: "Manzini Industrial and Training Centre (MITC)", category: "TVET College" },
    { label: "Ngwane Park TVET", category: "TVET College" },
    { label: "Bethel Vocational Centre", category: "TVET College" },
    { label: "Bosco Youth Agricultural Centre", category: "TVET College" },

    // Botswana TVETs
    { label: "Gaborone Technical College (GTC)", category: "TVET College" },
    { label: "Francistown College of Technical and Vocational Education (FCTVE)", category: "TVET College" },
    { label: "Oodi College of Applied Arts and Technology (OCAAT)", category: "TVET College" },
    { label: "Botswana Bible Training Institution", category: "TVET College" },
    { label: "Gaborone College of Culinary Arts", category: "TVET College" },
    { label: "College of Fire and Safety", category: "TVET College" },
    { label: "Gaborone Commercial College of Higher Studies", category: "TVET College" },

    // Mozambique TVETs
    { label: "Instituto Industrial e Comercial (Various Branches)", category: "TVET College" },
    { label: "Dom Bosco Middle Institute", category: "TVET College" },
    { label: "Agricultural Institute of Chimoio", category: "TVET College" },
    { label: "Chibuto Polytechnic Secondary Institute", category: "TVET College" },
    { label: "Indico Polytechnic Institute", category: "TVET College" },
    { label: "Institute of Health Sciences (Tete / Chimoio)", category: "TVET College" },
    { label: "Global Academy Technical Secondary Institute", category: "TVET College" },

    // ==========================================
    // --- CATCH-ALL FALLBACKS ---
    // ==========================================
    { label: "Other / Private Institution", category: "Other" },
    { label: "TVET College (Specify in notes)", category: "TVET College" }
];

const steps = ['Eligibility', 'POPIA', 'Personal & Address', 'Academic Info', 'Financial Aid', 'NUM Details', 'Documents', 'Review'];

const ApplicationFormJBMarks = ({ fundId: propFundId }) => {
    const { token } = useAuth();
    const navigate = useNavigate();
    
    const [searchParams] = useSearchParams();
    const fundId = propFundId || searchParams.get('fundId');

    const [activeStep, setActiveStep] = useState(0);
    const [profileData, setProfileData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [locating, setLocating] = useState(false);

    const [formData, setFormData] = useState({
        applicationId: null,
        personal: {}, address: {}, academic: { subjects: {} }, guardian: {}, financial: {}, 
        motivation: '', declaration: false, popia: {}, jbmarks: {}
    });

    const autoSaveTimerRef = useRef(null);

    useEffect(() => {
        const init = async () => {
            if (!token) return; 

            if (!fundId || fundId === 'null') {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const [profileRes, appRes, guardianRes, addressRes, academicRes] = await Promise.all([
                    api.get('/applicant/profile', { headers: { Authorization: `Bearer ${token}` } }),
                    api.get(`/applicant/application?fundId=${fundId}`, { headers: { Authorization: `Bearer ${token}` } }),
                    api.get('/applicant/guardian', { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
                    api.get('/applicant/address', { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
                    api.get('/applicant/academic', { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
                ]);

                const profile = profileRes.data || {};
                setProfileData({ ...profile, academicResults: academicRes.data || [] });

                let initialData = { ...formData };

                if (appRes.data) {
                    const { PersonalDetails, AcademicInfo, FinancialInfo, Motivation, Id, PopiaConsent, JBMarksData } = appRes.data;
                    initialData = {
                        applicationId: Id,
                        personal: typeof PersonalDetails === 'string' ? JSON.parse(PersonalDetails) : PersonalDetails || {},
                        address: (typeof PersonalDetails === 'string' ? JSON.parse(PersonalDetails).address : PersonalDetails?.address) || {},
                        academic: typeof AcademicInfo === 'string' ? JSON.parse(AcademicInfo) : AcademicInfo || { subjects: {} },
                        financial: typeof FinancialInfo === 'string' ? JSON.parse(FinancialInfo).financial : FinancialInfo?.financial || {},
                        guardian: typeof FinancialInfo === 'string' ? JSON.parse(FinancialInfo).guardian : FinancialInfo?.guardian || {},
                        motivation: Motivation || '',
                        popia: typeof PopiaConsent === 'string' ? JSON.parse(PopiaConsent) : PopiaConsent || {},
                        jbmarks: typeof JBMarksData === 'string' ? JSON.parse(JBMarksData) : JBMarksData || {},
                        declaration: false
                    };
                    if (!initialData.academic.subjects) initialData.academic.subjects = {};
                }

                initialData.personal = {
                    ...initialData.personal,
                    Surname: profile.Surname, FirstNames: profile.FirstNames,
                    IdNumber: profile.IdNumber, CellNumber: profile.CellNumber,
                    Gender: profile.Gender, Race: profile.Race, Province: profile.Province,
                    email: initialData.personal.email || profile.Email || ''
                };

                if (Object.keys(initialData.guardian).length === 0 && guardianRes.data?.Relation) {
                    initialData.guardian = {
                        relationToApplicant: guardianRes.data.Relation, firstNames: guardianRes.data.FirstName,
                        surname: guardianRes.data.Surname, cellNo: guardianRes.data.CellNumber,
                        email: guardianRes.data.Email, company: guardianRes.data.Employer
                    };
                }
                if (Object.keys(initialData.address).length === 0 && addressRes.data?.Street) {
                    initialData.address = {
                        physicalStreet: addressRes.data.Street, physicalSuburb: addressRes.data.Suburb,
                        physicalCity: addressRes.data.City, physicalCode: addressRes.data.PostalCode
                    };
                }

                setFormData(initialData);
            } catch (err) {
                console.error("Init error:", err);
                setSnackbar({ open: true, message: 'Error loading application.', severity: 'error' });
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [token, fundId]); 

    // 🚨 NEW: Smart Pre-population effect. If they are a NUM Member, copy their profile info over.
    useEffect(() => {
        if (activeStep === 5 && formData.jbmarks?.numStatus === 'Yes') {
            setFormData(prev => {
                // Only overwrite if the fields are currently empty to prevent wiping out manual edits
                const updatedGuardian = { ...prev.guardian };
                if (!updatedGuardian.firstNames) updatedGuardian.firstNames = profileData.FirstNames;
                if (!updatedGuardian.surname) updatedGuardian.surname = profileData.Surname;
                if (!updatedGuardian.idNumber) updatedGuardian.idNumber = profileData.IdNumber;
                
                return { ...prev, guardian: updatedGuardian };
            });
        }
    }, [activeStep, formData.jbmarks?.numStatus, profileData]);

    const saveDraft = useCallback(async (dataToSave = null) => {
        if (!fundId) return false;
        setSaving(true);
        try {
            const payload = dataToSave || formData; 
            const res = await api.post('/applicant/application', {
                formData: payload, isSubmitting: false, fundId
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            setLastSaved(new Date());
            if (res.data?.applicationId && !formData.applicationId) {
                setFormData(prev => ({ ...prev, applicationId: res.data.applicationId }));
            }
            return true;
        } catch (err) {
            setSnackbar({ open: true, message: 'Save Error: Database rejected the save.', severity: 'error' });
            return false;
        } finally {
            setSaving(false);
        }
    }, [fundId, token, formData]);

    const handleInputChange = (section, e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;

        setFormData(prev => {
            const updated = { 
                ...prev, 
                [section]: (section === 'motivation' || section === 'declaration') ? val : { ...prev[section], [name]: val } 
            };
            
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = setTimeout(() => saveDraft(updated), 2000);
            return updated;
        });

        if (errors[name]) setErrors(prev => { const n = {...prev}; delete n[name]; return n; });
    };

    const handleNext = async () => {
        if (saving || submitting) return;

        let currentErrors = {};
        let isValid = true;

        switch (activeStep) {
            case 0: // Eligibility
                if (!formData.jbmarks?.eligibilityAck) {
                    currentErrors.eligibilityAck = "You must acknowledge the terms.";
                    isValid = false;
                }
                if (formData.jbmarks?.eligibilityAck && !formData.jbmarks?.numStatus) {
                    currentErrors.numStatus = "Please select your NUM affiliation.";
                    isValid = false;
                }
                break;
            case 1: // POPIA
                if (!formData.popia?.fullName) { currentErrors.fullName = "Required"; isValid = false; }
                if (!formData.popia?.location) { currentErrors.location = "Required"; isValid = false; }
                if (!formData.popia?.signatureCheckbox) { currentErrors.signatureCheckbox = "You must consent."; isValid = false; }
                break;
            case 2: // Personal & Address
                if (!formData.personal?.email) { currentErrors.email = "Required"; isValid = false; }
                if (!formData.address?.physicalStreet || !formData.address?.physicalCity) {
                    setSnackbar({ open: true, message: 'Physical address is required.', severity: 'error' });
                    isValid = false;
                }
                break;
            case 3: // Academic
                if (!formData.academic?.currentStatus) { currentErrors.currentStatus = "Required"; isValid = false; }
                if (!formData.academic?.intendedInstitutionType) { currentErrors.intendedInstitutionType = "Required"; isValid = false; }
                if (!formData.academic?.intendedQualification) { currentErrors.intendedQualification = "Required"; isValid = false; }
                
                // Require Choice 1 for Course and Institution
                if (!formData.academic?.intendedInstitution1) { currentErrors.intendedInstitution1 = "Required"; isValid = false; }
                if (!formData.academic?.intendedCourse1) { currentErrors.intendedCourse1 = "Required"; isValid = false; }
                
                if (!formData.motivation) { currentErrors.motivation = "Required"; isValid = false; }
                break;
            case 4: // Financial Aid & Logistics
                // 🚨 Restored Validation for Financial Data
                if (!formData.financial?.householdIncome) { currentErrors.householdIncome = "Required"; isValid = false; }
                if (!formData.financial?.numberOfDependants) { currentErrors.numberOfDependants = "Required"; isValid = false; }

                if (!formData.financial?.acceptanceLetter) { currentErrors.acceptanceLetter = "Required"; isValid = false; }
                if (!formData.financial?.otherAid) { currentErrors.otherAid = "Required"; isValid = false; }
                if (formData.financial?.otherAid === 'Yes' && !formData.financial?.otherAidDate) { currentErrors.otherAidDate = "Required"; isValid = false; }
                if (!formData.financial?.needsAccommodation) { currentErrors.needsAccommodation = "Required"; isValid = false; }
                break;
            case 5: // NUM Details
                if (!formData.guardian?.surname) { currentErrors.surname = "Required"; isValid = false; }
                if (!formData.guardian?.firstNames) { currentErrors.firstNames = "Required"; isValid = false; }
                if (!formData.guardian?.idNumber) { currentErrors.idNumber = "Required"; isValid = false; }
                if (!formData.jbmarks?.region) { currentErrors.region = "Required"; isValid = false; }
                if (!formData.jbmarks?.branch) { currentErrors.branch = "Required"; isValid = false; }
                if (!formData.jbmarks?.companyName) { currentErrors.companyName = "Required"; isValid = false; }
                if (!formData.jbmarks?.industryNo) { currentErrors.industryNo = "Required"; isValid = false; }
                if (!formData.guardian?.industry) { currentErrors.industry = "Required"; isValid = false; }
                break;
            case 6: // Documents
                if (errors.documents) {
                    setSnackbar({ open: true, message: 'Please attach all required documents.', severity: 'error' });
                    isValid = false;
                }
                break;
            default: break;
        }

        if (!isValid) {
            setErrors(prev => ({ ...prev, ...currentErrors }));
            setSnackbar({ open: true, message: 'Validation failed. Please complete the fields highlighted in red.', severity: 'error' });
            return;
        }

        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

        const saveSuccess = await saveDraft(formData);
        if (!saveSuccess) return; 

        if (activeStep === steps.length - 1) {
            handleSubmitApplication();
        } else {
            setActiveStep(prev => prev + 1);
            window.scrollTo(0, 0);
        }
    };

    const handleSubmitApplication = async () => {
        if (!formData.declaration) {
            setSnackbar({ open: true, message: 'Please confirm the final declaration.', severity: 'warning' });
            return;
        }
        setSubmitting(true);
        try {
            await api.post('/applicant/application', { formData, isSubmitting: true, fundId }, { headers: { Authorization: `Bearer ${token}` } });
            navigate('/applicant/dashboard');
        } catch (err) {
            setSnackbar({ open: true, message: 'Submission failed.', severity: 'error' });
            setSubmitting(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
    
    if (!fundId || fundId === 'null') {
        return (
            <Container maxWidth="md" sx={{ mt: 10, textAlign: 'center' }}>
                <Alert severity="error" sx={{ mb: 3 }}>Missing Bursary ID! Please return to the dashboard and select a valid bursary.</Alert>
                <Button variant="contained" onClick={() => navigate('/applicant/dashboard')}>Back to Dashboard</Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="bold">JB Marks Bursary Application</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {saving && <Typography variant="caption">Saving...</Typography>}
                    {lastSaved && !saving && <Typography variant="caption" color="success.main">Saved {lastSaved.toLocaleTimeString()}</Typography>}
                    <Button startIcon={<SaveIcon />} onClick={() => saveDraft()} variant="outlined" size="small" disabled={saving}>Save Draft</Button>
                </Box>
            </Paper>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
            </Stepper>

            <Paper sx={{ p: 4, minHeight: '400px' }}>
                
                {/* --- STEP 0: ELIGIBILITY --- */}
                {activeStep === 0 && (
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary">Pre-Application Notices</Typography>
                        <Paper 
                            variant="outlined" 
                            sx={{ 
                                p: 3, 
                                mb: 3, 
                                maxHeight: '350px', 
                                overflowY: 'auto', 
                                // 👇 Read the theme mode directly and apply a subtle dynamic contrast background
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">NB! Please note the following before filling in the form: </Typography>
                            <ol style={{ paddingLeft: '20px' }}>
                                <li><Typography variant="body2">The bursary is open to NUM members and their dependents only (i.e. spouse and children).</Typography></li>
                                <li><Typography variant="body2">We pay out an amount which is determined by the course of studies of students who applied and meet the criteria depending on the availability of funds.</Typography></li>
                                <li><Typography variant="body2">Students who drop out within the first 6 months of study will be made to reimburse the bursary granted.</Typography></li>
                                <li><Typography variant="body2">Please check at your Regional Office if your name appears on the list of successful bursars at the beginning of December.</Typography></li>
                                <li><Typography variant="body2">If you have not received a JB Marks acceptance letter by the end of January or your name does not appear on the NUM regional list, you are unsuccessful.</Typography></li>
                                <li><Typography variant="body2">Closing date for all application forms is the 1st July of each year.</Typography></li>
                                <li><Typography variant="body2">Late and incomplete application forms will not be accepted.</Typography></li>
                                <li><Typography variant="body2">Should a beneficiary receive any other financial aid outside of the Trust, their bursary will be terminated and the beneficiary will be liable to reimburse the Trust all the monies already paid towards their studies.</Typography></li>
                                <li><Typography variant="body2">Indicate as to whether you are receiving any other financial assistance for your studies outside of the Trust.</Typography></li>
                                <li><Typography variant="body2">We provide funding only to Nationally recognized and registered Educational Institutions.</Typography></li>
                                <li><Typography variant="body2">Successful applicants are required to seek approval prior to changing their course or institution.</Typography></li>
                                <li><Typography variant="body2">We offer postgraduate studies to NUM members who have never been funded by the Trust.</Typography></li>
                                <li><Typography variant="body2">Please note that this bursary cannot be used to pay for existing loans or debts.</Typography></li>
                                <li><Typography variant="body2">We do not accept faxed or copies of the application form because each form has a track number for each region.</Typography></li>
                            </ol>
                            <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>NB: Proof of dependency</Typography>
                            <Typography variant="body2" paragraph>
                                In cases where the dependent's surname differs from the principal member, proof of dependency will be required, i.e. Marriage Certificate for Spouses, or Unabridged Birth Certificate, or Clinic Immunization Card, or Medical Aid Beneficiary Certificate or Magistrate Guardianship letter etc.
                            </Typography>
                        </Paper>

                        <FormControl error={!!errors.eligibilityAck} fullWidth>
                            <FormControlLabel
                                control={<Checkbox checked={formData.jbmarks?.eligibilityAck || false} onChange={(e) => handleInputChange('jbmarks', { target: { name: 'eligibilityAck', value: e.target.checked, type: 'checkbox', checked: e.target.checked } })} />}
                                label="I have read, understood, and acknowledge all JB Marks conditions." 
                            />
                            {errors.eligibilityAck && <FormHelperText>{errors.eligibilityAck}</FormHelperText>}
                        </FormControl>

                        {formData.jbmarks?.eligibilityAck && (
                            <FormControl component="fieldset" fullWidth error={!!errors.numStatus} sx={{ mt: 2 }}>
                                <FormLabel>Are you a NUM Member or a Staff member?</FormLabel>
                                <RadioGroup row name="numStatus" value={formData.jbmarks?.numStatus || ''} onChange={(e) => handleInputChange('jbmarks', e)}>
                                    <FormControlLabel value="Yes" control={<Radio />} label="Yes (NUM Member / Staff)" />
                                    <FormControlLabel value="No" control={<Radio />} label="No (Dependent)" />
                                </RadioGroup>
                                {errors.numStatus && <FormHelperText>{errors.numStatus}</FormHelperText>}
                            </FormControl>
                        )}
                    </Box>
                )}

                {/* --- STEP 1: POPIA --- */}
                {activeStep === 1 && (
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary">POPIA Consent</Typography>
                        <Paper variant="outlined" sx={{ p: 4, mb: 3, height: '400px', overflowY: 'auto', bgcolor: 'background.paper', borderColor: 'divider' }}>
                            <Typography variant="h6" align="center" gutterBottom>Personal Information Collection Consent</Typography>
                            <Typography variant="subtitle1" align="center" fontWeight="bold">JB Marks Education Trust Fund – Processing of Personal Information</Typography>
                            <Typography variant="subtitle2" align="center" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>Consent Form</Typography>
                            
                            <Typography variant="body2" paragraph>In terms of Protection of Personal Information Act 4 of 2013 (“POPIA”)</Typography>
                            <Typography variant="body2" paragraph>Consent to the processing of personal information of beneficiaries and related categories of persons in terms of POPIA</Typography>
                            
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>1. Application</Typography>
                            <Typography variant="body2" paragraph>This processing consent form applies to any person or entity who makes an application for assistance to the JB Marks Education Trust Fund (“the Fund”) or who is, or becomes, a beneficiary of the Fund.</Typography>
                            
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>2. Definitions</Typography>
                            <Typography variant="body2" paragraph><strong>2.1 "Personal Information"</strong> includes your name, address, contact details, date of birth, place of birth, identity number, passport number, bank details, tax number, financial information, next of kin, dependents, career, education, work performance and history, salary and employment-related information, nationality and immigration status, memberships or affiliations including professional data and similar data, which are required for contractual or employment-related matters or which are required to comply with laws and public duties.</Typography>
                            <Typography variant="body2" paragraph><strong>2.2 "Special Personal Information"</strong> includes your race, gender, pregnancy, national, ethnic or social origin, colour, physical or mental health, disability, criminal history, including offences committed or alleged to have been committed, and biometric information.</Typography>
                            <Typography variant="body2" paragraph><strong>2.3 "Processing"</strong> means generally the collection, receipt, storage, use, or dissemination or making available of personal or special personal information.</Typography>
                            <Typography variant="body2" paragraph><strong>2.4 "Third Party Service Provider"</strong> may include implementation agencies (who implement a learnership program), organisations who provide funding to the Fund, verification agencies, advertising and public relations agencies, service providers, auditors, legal practitioners, or organs of state, government, provincial and municipal bodies, who may process your personal or special personal information on our behalf.</Typography>

                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>3. How and from where is Personal Information Processed?</Typography>
                            <Typography variant="body2" paragraph>The Fund gathers your personal information directly from you; automatically when you use the Fund’s electronic equipment, devices, applications, portals or platforms, through the Fund’s security systems, when you submit applications for assistance to the Fund, and from third parties, including third party service providers.</Typography>

                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>4. Reason why the Personal Information is Processed</Typography>
                            <Typography variant="body2" paragraph>In terms of POPIA, your consent is required for the processing of personal information by the Fund (for the purposes described above) or where it is necessary for the Fund to carry out actions for the performance of a contract to which you are party, in order to comply with an obligation imposed by law, for a legitimate purpose or where necessary to protect or pursue the Fund’s legitimate interests.</Typography>

                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>5. How we secure your Personal Information</Typography>
                            <Typography variant="body2" paragraph><strong>5.1</strong> We will take all steps reasonably necessary to ensure that your personal information will be secured appropriately and will be used for the purposes indicated in this form.</Typography>
                            <Typography variant="body2" paragraph><strong>5.2</strong> Information will be destroyed after it is no longer required or where appropriate, retained for a longer period to continue with the purposes described in this consent form.</Typography>
                            <Typography variant="body2" paragraph><strong>5.3</strong> By submitting or providing your personal information to us or by applying for assistance from the Fund you agree to this transfer, storing or processing.</Typography>
                            <Typography variant="body2" paragraph><strong>5.4</strong> However, please note that no method of transmission over the Internet or method of electronic storage is 100% secure. Therefore, while we strive to use commercially acceptable measures designed to protect personal information, we cannot guarantee its absolute security.</Typography>

                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>6. Access to your Personal Information by others</Typography>
                            <Typography variant="body2" paragraph><strong>6.1</strong> The Fund will share your personal information with third parties, including third party service providers where required or permitted by law; where it is necessary to administer the contract and relationship with you; or in pursuit of a legitimate interest of the Fund.</Typography>
                            <Typography variant="body2" paragraph><strong>6.2</strong> The Fund will only share your personal information to the extent needed for those purposes.</Typography>

                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>7. Your Rights</Typography>
                            <Typography variant="body2" paragraph><strong>7.1</strong> You may contact our information officer at info@jbmarksedutrust.co.za to obtain access to the personal information; rectify the personal information, object to or restrict the further processing of the information, or withdraw your consent to process the information, in terms of POPIA.</Typography>
                            <Typography variant="body2" paragraph><strong>7.2</strong> You may also lodge a complaint with the Information Regulator.</Typography>

                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>8. Your Consent</Typography>
                            <Typography variant="body2" paragraph><strong>8.1</strong> By providing your personal information to us or by using our services, website or other online or digital platform(s) you are accepting or consenting to the practices as described or referred to in this privacy notice and you hereby give permission for your personal and special personal information to be processed.</Typography>
                            <Typography variant="body2" paragraph><strong>8.2</strong> You also confirm that you understand the purposes for which your personal or special personal information will be processed and you are aware that it will be processed for these purposes and no other.</Typography>
                            <Typography variant="body2" paragraph><strong>8.3</strong> You hereby declare that all the information supplied is accurate, not misleading and confirm that you will advise the Fund of any change.</Typography>
                            <Typography variant="body2" paragraph><strong>8.4</strong> Should any of the personal information belong to any of your dependants and/or beneficiaries who are underage, you in their capacity as their legal guardian and/or duly authorised and competent person give the Fund the necessary consent to process their personal information for the purposes for which these details were given.</Typography>

                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>9. Changes to this Consent Form</Typography>
                            <Typography variant="body2" paragraph>As the Fund changes over time, this consent form is expected to change as well. We reserve the right to amend this consent form at any time, for any reason, and without notice to you other than the posting of the updated consent form on our website and in this regard encourage you to visit our website frequently in order to keep abreast of any changes.</Typography>

                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>10. Contact Us</Typography>
                            <Typography variant="body2" paragraph><strong>Information Officer Details:</strong> APO Bongani Dlamini<br/>
                            <strong>Physical Address:</strong> MIT House, 35 Keyes Avenue, Rosebank, 2196<br/>
                            <strong>Postal Address:</strong> MIT House, 35 Keyes Avenue, Rosebank, 2196<br/>
                            <strong>Telephone Number:</strong> 0114920601<br/>
                            <strong>Email address:</strong> bongani@jbmarksedutrust.co.za</Typography>
                            <Typography variant="body2" paragraph>Any comments, questions or suggestions about this consent form or our handling of your personal information should be emailed to info@jbmarksedutrust.co.za.</Typography>

                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, color: 'error.main' }}>Consequences of Withholding Consent or Personal Information</Typography>
                            <Typography variant="body2" paragraph>Should you refuse to provide the Fund with your personal information, which information is required by the Fund for the purposes indicated above, together with the required and requisite consent to process the aforementioned personal information, then the Fund will be unable to engage with you and/or enter into any subsequent relationship with you.</Typography>
                            
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>11. Declaration</Typography>
                            <Typography variant="body2" paragraph>I, the undersigned, hereby consent to the processing of my information by JB Marks Education Trust Fund as described above.</Typography>
                        </Paper>

                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Full Names" name="fullName" value={formData.popia?.fullName || ''} onChange={(e) => handleInputChange('popia', e)} error={!!errors.fullName} helperText={errors.fullName} required />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField fullWidth label="Location (Signed At)" name="location" value={formData.popia?.location || ''} onChange={(e) => handleInputChange('popia', e)} error={!!errors.location} helperText={errors.location} required InputProps={{ readOnly: locating }} />
                                    <Button variant="outlined" disabled={locating} onClick={() => {
                                        if (!navigator.geolocation) return setSnackbar({ open: true, message: 'Geolocation is not supported.', severity: 'error' });
                                        setLocating(true);
                                        navigator.geolocation.getCurrentPosition(async (position) => {
                                            try {
                                                const res = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`);
                                                const data = await res.json();
                                                handleInputChange('popia', { target: { name: 'location', value: `${data.city}, ${data.countryName}` } });
                                            } catch (err) {
                                                handleInputChange('popia', { target: { name: 'location', value: `Lat: ${position.coords.latitude.toFixed(2)}, Lng: ${position.coords.longitude.toFixed(2)}` } });
                                            } finally { setLocating(false); }
                                        });
                                    }}>
                                        {locating ? <CircularProgress size={24} /> : <MyLocationIcon />}
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl error={!!errors.signatureCheckbox}>
                                    <FormControlLabel control={<Checkbox name="signatureCheckbox" checked={formData.popia?.signatureCheckbox || false} onChange={(e) => handleInputChange('popia', { target: { name: 'signatureCheckbox', value: e.target.checked, type: 'checkbox', checked: e.target.checked }})} />} label="I accept and consent to the POPIA terms on behalf of my digital signature." />
                                    {errors.signatureCheckbox && <FormHelperText>{errors.signatureCheckbox}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* --- STEP 2: PERSONAL & ADDRESS --- */}
                {/* --- STEP 2: PERSONAL & ADDRESS --- */}
{activeStep === 2 && (
    <Box>
        <Typography variant="h6" gutterBottom color="primary">Applicant Details</Typography>
        <Grid container spacing={3}>
            {/* 🚨 THE FIX: Removed the 'disabled' prop so users can heal their wiped names! */}
            <Grid item xs={12} sm={4}>
                <TextField 
                    required 
                    label="Surname" 
                    name="Surname"
                    fullWidth 
                    value={formData.personal?.Surname || profileData.Surname || ''} 
                    onChange={(e) => handleInputChange('personal', e)}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField 
                    name="maidenName" 
                    label="Maiden Surname (If applicable)" 
                    fullWidth 
                    value={formData.personal.maidenName || ''} 
                    onChange={(e) => handleInputChange('personal', e)} 
                />
            </Grid>
            {/* 🚨 THE FIX: Removed the 'disabled' prop */}
            <Grid item xs={12} sm={4}>
                <TextField 
                    required 
                    label="First Names" 
                    name="FirstNames"
                    fullWidth 
                    value={formData.personal?.FirstNames || profileData.FirstNames || ''} 
                    onChange={(e) => handleInputChange('personal', e)}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField 
                    disabled 
                    label="ID or Passport Number" 
                    fullWidth 
                    value={profileData.IdNumber || ''} 
                />
            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required error={!!errors.tShirtSize}>
                                    <InputLabel>T-Shirt Size</InputLabel>
                                    <Select name="tShirtSize" value={formData.personal.tShirtSize || ''} onChange={(e) => handleInputChange('personal', e)}>
                                        {['Small', 'Medium', 'Large', 'X-Large', 'XX-Large'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField required name="country" label="Country" fullWidth value={formData.personal.country || 'South Africa'} onChange={(e) => handleInputChange('personal', e)} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField name="homePhone" label="Home Telephone" fullWidth value={formData.personal.homePhone || ''} onChange={(e) => handleInputChange('personal', e)} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField required name="email" label="Contact Email" type="email" fullWidth value={formData.personal.email || ''} onChange={(e) => handleInputChange('personal', e)} error={!!errors.email} helperText={errors.email} />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 4 }} />
                        <Typography variant="subtitle1" color="primary" gutterBottom>Address for Correspondence</Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={4}><TextField required name="physicalUnit" label="House/Unit Number" fullWidth value={formData.address.physicalUnit || ''} onChange={(e) => handleInputChange('address', e)} /></Grid>
                            <Grid item xs={12} sm={8}><TextField required name="physicalStreet" label="Street Name" fullWidth value={formData.address.physicalStreet || ''} onChange={(e) => handleInputChange('address', e)} /></Grid>
                            <Grid item xs={12} sm={4}><TextField required name="physicalTown" label="Town" fullWidth value={formData.address.physicalTown || ''} onChange={(e) => handleInputChange('address', e)} /></Grid>
                            <Grid item xs={12} sm={4}><TextField required name="physicalCity" label="City" fullWidth value={formData.address.physicalCity || ''} onChange={(e) => handleInputChange('address', e)} /></Grid>
                            <Grid item xs={12} sm={2}><TextField required name="physicalCode" label="Postal Code" fullWidth value={formData.address.physicalCode || ''} onChange={(e) => handleInputChange('address', e)} /></Grid>
                            <Grid item xs={12} sm={2}>
                                <FormControl fullWidth required>
                                    <InputLabel>Province / SADC Country</InputLabel>
                                    <Select 
                                        name="physicalProvince" 
                                        value={formData.address.physicalProvince || ''} 
                                        onChange={(e) => handleInputChange('address', e)}
                                        label="Province / SADC Country"
                                    >
                                        {LOCATION_OPTIONS.map((loc, index) => (
                                            <MenuItem 
                                                key={index} 
                                                value={loc.value || ''} 
                                                disabled={loc.isHeader}
                                                sx={loc.isHeader ? { 
                                                    opacity: 1, 
                                                    fontWeight: 'bold', 
                                                    color: 'primary.main', 
                                                    justifyContent: 'center',
                                                    bgcolor: 'action.hover',
                                                    pointerEvents: 'none' 
                                                } : {}}
                                            >
                                                {loc.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        
                        <FormControlLabel sx={{ mt: 2, mb: 2 }} control={<Checkbox checked={formData.address.sameAsResidential || false} onChange={(e) => {
                            const isChecked = e.target.checked;
                            handleInputChange('address', { target: { name: 'sameAsResidential', value: isChecked, type: 'checkbox', checked: isChecked }});
                            if (isChecked) {
                                handleInputChange('address', { target: { name: 'hasPostalBag', value: 'No' }});
                                handleInputChange('address', { target: { name: 'postalBox', value: formData.address.physicalUnit ? `${formData.address.physicalUnit} ${formData.address.physicalStreet}` : formData.address.physicalStreet }});
                                handleInputChange('address', { target: { name: 'postalTown', value: formData.address.physicalTown }});
                                handleInputChange('address', { target: { name: 'postalCity', value: formData.address.physicalCity }});
                                handleInputChange('address', { target: { name: 'postalCode', value: formData.address.physicalCode }});
                            } else {
                                handleInputChange('address', { target: { name: 'postalBox', value: '' }});
                            }
                        }} />} label="Postal Address is the same as Residential" />

                        {!formData.address.sameAsResidential && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <FormControl component="fieldset">
                                        <FormLabel>Do you have a P.O. Box / Private Bag number?</FormLabel>
                                        <RadioGroup row name="hasPostalBag" value={formData.address.hasPostalBag || 'No'} onChange={(e) => handleInputChange('address', e)}>
                                            <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                            <FormControlLabel value="No" control={<Radio />} label="No" />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}><TextField required name="postalBox" label="P.O. Box/Street" fullWidth value={formData.address.postalBox || ''} onChange={(e) => handleInputChange('address', e)} /></Grid>
                                <Grid item xs={12} sm={4}><TextField required name="postalTown" label="Town" fullWidth value={formData.address.postalTown || ''} onChange={(e) => handleInputChange('address', e)} /></Grid>
                                <Grid item xs={12} sm={4}><TextField required name="postalCity" label="City" fullWidth value={formData.address.postalCity || ''} onChange={(e) => handleInputChange('address', e)} /></Grid>
                                <Grid item xs={12} sm={4}><TextField required name="postalCode" label="Postal Code" fullWidth value={formData.address.postalCode || ''} onChange={(e) => handleInputChange('address', e)} /></Grid>
                            </Grid>
                        )}                        
                    </Box>
                )}

                {/* --- STEP 3: ACADEMIC INFO --- */}
                {activeStep === 3 && (
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary">Education & Intended Studies</Typography>
                        
                        {/* Current Status Block */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <FormControl component="fieldset" required error={!!errors.currentStatus}>
                                    <FormLabel>Are you currently studying?</FormLabel>
                                    <RadioGroup name="currentStatus" value={formData.academic?.currentStatus || ''} onChange={(e) => handleInputChange('academic', e)}>
                                        <FormControlLabel value="highSchool" control={<Radio />} label="Yes, in High School" />
                                        <FormControlLabel value="tertiary" control={<Radio />} label="Yes, at a Tertiary Institution" />
                                        <FormControlLabel value="notStudying" control={<Radio />} label="No, I am not currently studying" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={8}>
                                {formData.academic?.currentStatus === 'highSchool' && (
                                    <TextField required name="currentInstitutionName" label="Name of High School" fullWidth value={formData.academic?.currentInstitutionName || ''} onChange={(e) => handleInputChange('academic', e)} />
                                )}

                                {formData.academic?.currentStatus === 'tertiary' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth required>
                                                <InputLabel>Type of Institution</InputLabel>
                                                <Select name="currentInstitutionType" value={formData.academic?.currentInstitutionType || ''} onChange={(e) => handleInputChange('academic', e)}>
                                                    {INSTITUTION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField required name="currentInstitutionName" label="Name of Institution" fullWidth value={formData.academic?.currentInstitutionName || ''} onChange={(e) => handleInputChange('academic', e)} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField required name="currentCourse" label="Course currently studying" fullWidth value={formData.academic?.currentCourse || ''} onChange={(e) => handleInputChange('academic', e)} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth required>
                                                <InputLabel>Current Level of Study</InputLabel>
                                                <Select name="currentLevel" value={formData.academic?.currentLevel || ''} onChange={(e) => handleInputChange('academic', e)}>
                                                    <MenuItem value="1st Year">1st Year</MenuItem>
                                                    <MenuItem value="2nd Year">2nd Year</MenuItem>
                                                    <MenuItem value="3rd Year">3rd Year</MenuItem>
                                                    <MenuItem value="4th Year">4th Year</MenuItem>
                                                    <MenuItem value="5th Year">5th Year</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                )}

                                {formData.academic?.currentStatus === 'notStudying' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth required>
                                                <InputLabel>Highest High School Qualification</InputLabel>
                                                <Select name="highestHSQualification" value={formData.academic?.highestHSQualification || ''} onChange={(e) => handleInputChange('academic', e)}>
                                                    <MenuItem value="Grade 8">Grade 8</MenuItem><MenuItem value="Grade 9">Grade 9</MenuItem>
                                                    <MenuItem value="Grade 10">Grade 10</MenuItem><MenuItem value="Grade 11">Grade 11</MenuItem>
                                                    <MenuItem value="Grade 12">Grade 12</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField required name="yearAchievedHS" label="Year Achieved" fullWidth value={formData.academic?.yearAchievedHS || ''} onChange={(e) => handleInputChange('academic', e)} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField name="otherQualification" label="Other Qualification (if any)" fullWidth value={formData.academic?.otherQualification || ''} onChange={(e) => handleInputChange('academic', e)} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField name="yearAchievedOther" label="Year Achieved (Other)" fullWidth value={formData.academic?.yearAchievedOther || ''} onChange={(e) => handleInputChange('academic', e)} />
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 4 }} />
                        
                        <Typography variant="subtitle1" color="primary" gutterBottom>Intended Studies (Next Year)</Typography>
                        
                        <Grid container spacing={3} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth required error={!!errors.intendedInstitutionType}>
                                    <InputLabel>Type of Institution</InputLabel>
                                    <Select name="intendedInstitutionType" value={formData.academic?.intendedInstitutionType || ''} onChange={(e) => handleInputChange('academic', e)}>
                                        {INSTITUTION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth required error={!!errors.intendedQualification}>
                                    <InputLabel>Type of Qualification</InputLabel>
                                    <Select name="intendedQualification" value={formData.academic?.intendedQualification || ''} onChange={(e) => handleInputChange('academic', e)}>
                                        {QUALIFICATION_TYPES.map(q => <MenuItem key={q} value={q}>{q}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth required>
                                    <InputLabel>Intended Level of Study</InputLabel>
                                    <Select name="intendedLevel" value={formData.academic?.intendedLevel || ''} onChange={(e) => handleInputChange('academic', e)}>
                                        <MenuItem value="1st Year">1st Year</MenuItem>
                                        <MenuItem value="2nd Year">2nd Year</MenuItem>
                                        <MenuItem value="3rd Year">3rd Year</MenuItem>
                                        <MenuItem value="4th Year">4th Year</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* 🚨 3 CHOICES FOR COURSE AND INSTITUTION */}
                        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Please provide up to 3 choices for your intended Course and Institution. Choice 1 is mandatory.
                            </Typography>
                            
                            <Grid container spacing={3}>
                                {/* CHOICE 1 */}
                                <Grid item xs={12} sx={{ pb: 0 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">Choice 1 (Primary)</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={INSTITUTIONS.sort((a, b) => -b.category.localeCompare(a.category))}
                                        groupBy={(option) => option.category}
                                        getOptionLabel={(option) => option.label}
                                        value={INSTITUTIONS.find(i => i.label === formData.academic?.intendedInstitution1) || null}
                                        onChange={(e, newValue) => handleInputChange('academic', { target: { name: 'intendedInstitution1', value: newValue ? newValue.label : '' }})}
                                        renderInput={(params) => <TextField {...params} label="Institution" fullWidth required error={!!errors.intendedInstitution1} />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField required name="intendedCourse1" label="Course Name" fullWidth value={formData.academic?.intendedCourse1 || ''} onChange={(e) => handleInputChange('academic', e)} error={!!errors.intendedCourse1} />
                                </Grid>

                                {/* CHOICE 2 */}
                                <Grid item xs={12} sx={{ pb: 0 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">Choice 2</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={INSTITUTIONS.sort((a, b) => -b.category.localeCompare(a.category))}
                                        groupBy={(option) => option.category}
                                        getOptionLabel={(option) => option.label}
                                        value={INSTITUTIONS.find(i => i.label === formData.academic?.intendedInstitution2) || null}
                                        onChange={(e, newValue) => handleInputChange('academic', { target: { name: 'intendedInstitution2', value: newValue ? newValue.label : '' }})}
                                        renderInput={(params) => <TextField {...params} label="Institution" fullWidth />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField name="intendedCourse2" label="Course Name" fullWidth value={formData.academic?.intendedCourse2 || ''} onChange={(e) => handleInputChange('academic', e)} />
                                </Grid>

                                {/* CHOICE 3 */}
                                <Grid item xs={12} sx={{ pb: 0 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">Choice 3</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={INSTITUTIONS.sort((a, b) => -b.category.localeCompare(a.category))}
                                        groupBy={(option) => option.category}
                                        getOptionLabel={(option) => option.label}
                                        value={INSTITUTIONS.find(i => i.label === formData.academic?.intendedInstitution3) || null}
                                        onChange={(e, newValue) => handleInputChange('academic', { target: { name: 'intendedInstitution3', value: newValue ? newValue.label : '' }})}
                                        renderInput={(params) => <TextField {...params} label="Institution" fullWidth />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField name="intendedCourse3" label="Course Name" fullWidth value={formData.academic?.intendedCourse3 || ''} onChange={(e) => handleInputChange('academic', e)} />
                                </Grid>
                            </Grid>
                        </Paper>
                        
                        <Divider sx={{ my: 4 }} />
                        <Typography variant="subtitle1" color="primary" gutterBottom>Motivation</Typography>
                        <TextField required name="motivation" label="Tell us why you chose this particular field of study? (Max 1000 chars)" multiline rows={4} fullWidth value={formData.motivation || ''} onChange={(e) => handleInputChange('motivation', e)} error={!!errors.motivation} helperText={errors.motivation} inputProps={{ maxLength: 1000 }} />
                    </Box>
                )}

                {/* --- STEP 4: FINANCIAL AID --- */}
                {activeStep === 4 && (
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary">Financial Aid & Logistics</Typography>
                        
                        <Grid container spacing={4}>
                            
                            {/* 🚨 NEW: Restored Financial Collection Fields 
                            🚨 THE FIX: Standardized Annual Household Income Dropdown */}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required error={!!errors.householdIncome}>
                                    <InputLabel>Gross Annual Household Income</InputLabel>
                                    <Select 
                                        name="householdIncome" 
                                        value={formData.financial?.householdIncome || ''} 
                                        onChange={(e) => handleInputChange('financial', e)}
                                        label="Gross Annual Household Income"
                                    >
                                        <MenuItem value="0 - 150000">Below R 150,000</MenuItem>
                                        <MenuItem value="150001 - 350000">R 150,001 - R 350,000</MenuItem>
                                        <MenuItem value="350001 - 600000">R 350,001 - R 600,000</MenuItem>
                                        <MenuItem value="600001+">Above R 600,000</MenuItem>
                                    </Select>
                                    {errors.householdIncome && <FormHelperText>{errors.householdIncome}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    required 
                                    name="numberOfDependants" 
                                    label="Number of Dependants" 
                                    type="number"
                                    fullWidth 
                                    value={formData.financial?.numberOfDependants || ''} 
                                    onChange={(e) => handleInputChange('financial', e)} 
                                    error={!!errors.numberOfDependants} 
                                    helperText={errors.numberOfDependants} 
                                />
                            </Grid>
                            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                            <Grid item xs={12}>
                                <FormControl component="fieldset" required error={!!errors.acceptanceLetter}>
                                    <FormLabel>Have you received your Acceptance letter from the institutions you have applied to?</FormLabel>
                                    <RadioGroup name="acceptanceLetter" value={formData.financial?.acceptanceLetter || ''} onChange={(e) => handleInputChange('financial', e)}>
                                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="No" control={<Radio />} label="No" />
                                        <FormControlLabel value="Not Applied" control={<Radio />} label="Have not yet applied at any institution" />
                                    </RadioGroup>
                                    {errors.acceptanceLetter && <FormHelperText>{errors.acceptanceLetter}</FormHelperText>}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl component="fieldset" required error={!!errors.otherAid}>
                                    <FormLabel>Are you currently receiving any other Financial Aid?</FormLabel>
                                    <RadioGroup row name="otherAid" value={formData.financial?.otherAid || ''} onChange={(e) => handleInputChange('financial', e)}>
                                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="No" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {formData.financial?.otherAid === 'Yes' && (
                                <Grid item xs={12} sm={6}>
                                    <TextField required type="date" name="otherAidDate" label="From when?" fullWidth InputLabelProps={{ shrink: true }} value={formData.financial?.otherAidDate || ''} onChange={(e) => handleInputChange('financial', e)} error={!!errors.otherAidDate} />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <FormControl component="fieldset" required error={!!errors.needsAccommodation}>
                                    <FormLabel>Would you require accommodation? (NB: Accommodation will only be provided to the applicant who has requested it.)</FormLabel>
                                    <RadioGroup row name="needsAccommodation" value={formData.financial?.needsAccommodation || ''} onChange={(e) => handleInputChange('financial', e)}>
                                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="No" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* --- STEP 5: NUM DETAILS --- */}
                {activeStep === 5 && (
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary">NUM Member or Staff Details</Typography>
                        
                        {/* 🚨 UI HINT FOR PRE-FILL */}
                        {formData.jbmarks?.numStatus === 'Yes' && (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                Since you are the NUM Member, your personal details have been automatically filled in from your profile.
                            </Alert>
                        )}

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}><TextField required name="surname" label="Member Surname" fullWidth value={formData.guardian.surname || ''} onChange={(e) => handleInputChange('guardian', e)} error={!!errors.surname} helperText={errors.surname} /></Grid>
                            <Grid item xs={12} sm={6}><TextField name="maidenName" label="Maiden Surname (Optional)" fullWidth value={formData.guardian.maidenName || ''} onChange={(e) => handleInputChange('guardian', e)} /></Grid>
                            <Grid item xs={12} sm={6}><TextField required name="firstNames" label="First Names in Full" fullWidth value={formData.guardian.firstNames || ''} onChange={(e) => handleInputChange('guardian', e)} error={!!errors.firstNames} helperText={errors.firstNames} /></Grid>
                            <Grid item xs={12} sm={6}><TextField required name="idNumber" label="Identity or Passport Number" fullWidth value={formData.guardian.idNumber || ''} onChange={(e) => handleInputChange('guardian', e)} error={!!errors.idNumber} helperText={errors.idNumber} /></Grid>
                            
                            <Grid item xs={12} sm={6}>
                                <TextField required name="country" label="Country" fullWidth value={formData.guardian.country || 'South Africa'} onChange={(e) => handleInputChange('guardian', e)} />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required error={!!errors.region}>
                                    <InputLabel>Which Region are you from?</InputLabel>
                                    <Select name="region" value={formData.jbmarks.region || ''} onChange={(e) => handleInputChange('jbmarks', e)}>
                                        {NUM_REGIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} sm={6}><TextField required name="branch" label="Branch Name" fullWidth value={formData.jbmarks.branch || ''} onChange={(e) => handleInputChange('jbmarks', e)} error={!!errors.branch} /></Grid>

                            <Grid item xs={12} sm={6}><TextField name="homePhone" label="Home Telephone" fullWidth value={formData.guardian.homePhone || ''} onChange={(e) => handleInputChange('guardian', e)} /></Grid>
                            <Grid item xs={12} sm={6}><TextField name="cellNo" label="Mobile Number" fullWidth value={formData.guardian.cellNo || ''} onChange={(e) => handleInputChange('guardian', e)} /></Grid>
                            <Grid item xs={12} sm={6}><TextField name="email" label="Member Email" type="email" fullWidth value={formData.guardian.email || ''} onChange={(e) => handleInputChange('guardian', e)} /></Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="subtitle1" color="primary">Employment Details</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}><TextField required name="companyName" label="Name of Company" fullWidth value={formData.jbmarks.companyName || ''} onChange={(e) => handleInputChange('jbmarks', e)} error={!!errors.companyName} /></Grid>
                            <Grid item xs={12} sm={6}><TextField required name="industryNo" label="Your Industry Number" fullWidth value={formData.jbmarks.industryNo || ''} onChange={(e) => handleInputChange('jbmarks', e)} error={!!errors.industryNo} /></Grid>
                            <Grid item xs={12} sm={6}><TextField name="companyPhone" label="Company Telephone Number" fullWidth value={formData.jbmarks.companyPhone || ''} onChange={(e) => handleInputChange('jbmarks', e)} /></Grid>
                            <Grid item xs={12} sm={6}><TextField name="companyFax" label="Company Fax Number" fullWidth value={formData.jbmarks.companyFax || ''} onChange={(e) => handleInputChange('jbmarks', e)} /></Grid>
                            
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required error={!!errors.industry}>
                                    <InputLabel>Please indicate the nature of your industry</InputLabel>
                                    <Select name="industry" value={formData.guardian.industry || ''} onChange={(e) => handleInputChange('guardian', e)}>
                                        {['Construction', 'Energy', 'Metals', 'Mining'].map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* --- STEP 6: DOCUMENTS --- */}
                {activeStep === 6 && (
                    <JBMarksDocumentVault 
                        applicationId={formData.applicationId} 
                        token={token} 
                        setErrors={setErrors}
                        setSnackbar={setSnackbar} 
                    />
                )}

                {/* --- STEP 7: REVIEW --- */}
                {activeStep === 7 && (
                    <ReviewStep 
                        formData={formData} 
                        handleInputChange={handleInputChange} 
                        errors={errors} 
                        profileData={profileData} 
                    />
                )}

                {/* --- NAVIGATION --- */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                    <Button disabled={activeStep === 0 || submitting} onClick={() => { setActiveStep(p => p - 1); window.scrollTo(0,0); }}>Back</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleNext} 
                        disabled={submitting || saving} 
                        endIcon={(submitting || saving) ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {activeStep === steps.length - 1 ? 'Submit Final Application' : 'Next'}
                    </Button>
                </Box>

            </Paper>
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

// ============================================================================
// EMBEDDED COMPONENT: JB MARKS DOCUMENT VAULT
// ============================================================================
// ============================================================================
// EMBEDDED COMPONENT: JB MARKS DOCUMENT VAULT (Upgraded with Replace & Delete)
// ============================================================================
const JBMarksDocumentVault = ({ applicationId, token, setErrors, setSnackbar }) => {
    const [personalDocs, setPersonalDocs] = useState([]);
    const [appDocs, setAppDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadingType, setUploadingType] = useState(null); 

    const docTypes = [
        { type: 'NUM_MEMBER_ID', label: 'Certified copy of NUM Member Identity Document' },
        { type: 'ID_DOCUMENT', label: 'Certified copy of the applicant Identity Document' },
        { type: 'ACADEMIC_RESULTS', label: 'Certified Latest Grade 12 Results or Latest Tertiary Results' },
        { type: 'NUM_PAYSLIP', label: 'Certified Latest payslip for verification of NUM Membership' },
        { type: 'ACCEPTANCE_LETTER', label: 'Proof of acceptance from the institution you\'ve applied to' },
        { type: 'PROOF_OF_FINANCIAL_AID', label: 'Proof of Financial Aid (If applicable)' },
        { type: 'PROOF_OF_DEPENDENCY', label: 'Proof of dependency - Marriage Certificate / Birth Certificate / etc.' },
        { type: 'PROOF_OF_ADDRESS', label: 'Certified Proof of Address' },
        { type: 'TEBA_FORM', label: 'TEBA Nomination Form (If applicable)' }
    ];

    const fetchDocs = useCallback(async () => {
        if (!applicationId) return;
        try {
            // 🚀 CACHE BUSTER: Generates a unique timestamp to force a fresh network request every time
            const timestamp = new Date().getTime();
            
            const [vaultRes, appRes] = await Promise.all([
                api.get(`/applicant/documents?t=${timestamp}`, { headers: { Authorization: `Bearer ${token}` } }),
                api.get(`/applicant/application/${applicationId}/documents?t=${timestamp}`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setPersonalDocs(vaultRes.data || []);
            setAppDocs(appRes.data || []);
        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    }, [applicationId, token]);

    useEffect(() => { fetchDocs(); }, [fetchDocs]);

    useEffect(() => {
        const requiredTypes = docTypes.filter(d => 
            d.type !== 'PROOF_OF_FINANCIAL_AID' && 
            d.type !== 'TEBA_FORM' &&
            d.type !== 'PROOF_OF_DEPENDENCY' 
        );
        const missing = requiredTypes.filter(req => !appDocs.some(d => d.DocumentType === req.type || d.FileType === req.type));
        
        if (missing.length > 0) setErrors(prev => ({ ...prev, documents: true }));
        else setErrors(prev => { const n = {...prev}; delete n.documents; return n; });
    }, [appDocs, setErrors]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUpload = async (file, docType, docLabel) => {
        if (!file) return;
        if (!applicationId) {
            setSnackbar({ open: true, message: 'Please wait for the application to auto-save before uploading.', severity: 'warning' });
            return;
        }

        setUploadingType(docType);
        
        const fData = new FormData();
        fData.append('documentType', docType);
        fData.append('documentLabel', docLabel || docType || 'Uploaded Document'); 
        fData.append('applicationId', applicationId);
        fData.append('document', file); 
        
        try {
            await api.post('/applicant/documents', fData, { 
                headers: { 
                    'Content-Type': 'multipart/form-data', 
                    Authorization: `Bearer ${token}` 
                }
            });
            await fetchDocs();
            setSnackbar({ open: true, message: 'Document uploaded securely.', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to upload document. Please check file size.', severity: 'error' });
        } finally {
            setUploadingType(null);
        }
    };

    const handleAttach = async (userDocumentId) => {
        setUploadingType(userDocumentId); 
        try {
            await api.post('/applicant/attach-document', { userDocumentId, applicationId }, { headers: { Authorization: `Bearer ${token}` }});
            await fetchDocs();
            setSnackbar({ open: true, message: 'Vault document attached securely.', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to attach document.', severity: 'error' });
        } finally {
            setUploadingType(null);
        }
    };

    // 🚨 NEW: Delete Document Function
    const handleDeleteDocument = async (docId, docType) => {
        setUploadingType(docType); // Show loader for this row
        try {
            // Delete the document from the ApplicationDocuments bridging table
            await api.delete(`/applicant/documents/${docId}`, { headers: { Authorization: `Bearer ${token}` }});
            await fetchDocs();
            setSnackbar({ open: true, message: 'Document removed successfully.', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to remove document.', severity: 'error' });
        } finally {
            setUploadingType(null);
        }
    };

    if (!applicationId) return <Alert severity="warning">Save your application as a draft first to enable document uploads.</Alert>;
    if (loading) return <CircularProgress />;

    return (
        <Box>
            <Typography variant="h6" gutterBottom color="primary">Document Checklist (PDF Only)</Typography>
            <List>
                {docTypes.map(doc => {
                    const attached = appDocs.find(d => d.DocumentType === doc.type || d.FileType === doc.type);
                    const inVault = personalDocs.find(d => d.DocumentType === doc.type || d.FileType === doc.type);
                    const isWorking = uploadingType === doc.type || uploadingType === inVault?.Id;

                    return (
                        <ListItem key={doc.type} divider sx={{ flexWrap: 'wrap', gap: 2 }}>
                            <ListItemIcon>{attached ? <CheckCircleIcon color="success" /> : <UploadFileIcon />}</ListItemIcon>
                            <ListItemText 
                                primary={doc.label} 
                                secondary={attached ? `Attached: ${attached.FileName}` : inVault ? 'Available in Vault' : 'Required'}
                                secondaryTypographyProps={{ color: attached ? 'success.main' : 'text.secondary' }}
                            />
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {isWorking ? <CircularProgress size={24} /> : attached ? (
                                    <>
                                        {/* 🚨 UPDATED: The Replace functionality (Delete & Re-upload) */}
                                        <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteDocument(attached.Id, doc.type)}>
                                            Remove
                                        </Button>
                                        <Button variant="contained" component="label" size="small" color="primary">
                                            Replace
                                            <input 
                                                type="file" 
                                                accept="application/pdf" 
                                                hidden 
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        await handleDeleteDocument(attached.Id, doc.type);
                                                        await handleUpload(file, doc.type, doc.label);
                                                    }
                                                    e.target.value = null; // Prevent Silent Freeze
                                                }} 
                                            />
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        {inVault && (
                                            <Button size="small" variant="outlined" startIcon={<AttachFileIcon />} onClick={() => handleAttach(inVault.Id)}>
                                                Attach Vault
                                            </Button>
                                        )}
                                        <Button variant="contained" component="label" size="small">
                                            Upload
                                            <input 
                                                type="file" 
                                                accept="application/pdf" 
                                                hidden 
                                                onChange={(e) => {
                                                    handleUpload(e.target.files?.[0], doc.type, doc.label);
                                                    e.target.value = null; // Prevent Silent Freeze
                                                }} 
                                            />
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
};

export default ApplicationFormJBMarks;